import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FiAlertTriangle, FiPause, FiPlay } from 'react-icons/fi';
import { apiJson, ApiError } from '@/lib/api';
import { track } from '@/lib/analytics';
import type {
  AdsOpsAction,
  AdsOpsActionsResponse,
  AdsOpsAllowedKeyword,
  AdsOpsPolicyDigest,
  AdsOpsScorecard,
  AdsOpsSearchTermsResponse,
  AdsOpsSettings,
  AdsOpsSnapshotsResponse,
  AdsOpsStandingOrdersResponse,
} from '@/lib/types';

const AD_GROUP_ORDER = ['expense', 'calendar', 'intent', 'tools', 'competitor'];

function titleCaseGroup(name: string): string {
  return name
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function groupAllowedKeywords(rows: AdsOpsAllowedKeyword[]) {
  const byGroup = new Map<string, Map<string, string[]>>();
  for (const row of rows) {
    let texts = byGroup.get(row.ad_group);
    if (!texts) {
      texts = new Map();
      byGroup.set(row.ad_group, texts);
    }
    const matches = texts.get(row.text) ?? [];
    if (!matches.includes(row.match)) matches.push(row.match);
    texts.set(row.text, matches);
  }
  return [...byGroup.entries()]
    .sort((a, b) => {
      const ia = AD_GROUP_ORDER.indexOf(a[0]);
      const ib = AD_GROUP_ORDER.indexOf(b[0]);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    })
    .map(([name, texts]) => ({
      name,
      keywords: [...texts.entries()].map(([text, matches]) => ({ text, matches })),
    }));
}

function money(n: number | null | undefined, digits = 2): string {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: digits,
  }).format(n);
}

function microsUsd(micros: number | null | undefined): string {
  if (micros == null) return '—';
  return money(micros / 1_000_000);
}

function cacTone(cac: number | null, high: number): 'good' | 'warn' | 'alert' | 'default' {
  if (cac == null) return 'default';
  if (cac <= high) return 'good';
  if (cac <= high * 1.5) return 'warn';
  return 'alert';
}

function Tile({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'good' | 'warn' | 'alert' | 'highlight';
}) {
  const cls =
    tone === 'alert'
      ? 'border-rose-300 bg-rose-50'
      : tone === 'warn'
        ? 'border-amber-300 bg-amber-50'
        : tone === 'good'
          ? 'border-emerald-200 bg-emerald-50'
          : tone === 'highlight'
            ? 'border-nova-primary/30 bg-nova-accent'
            : 'border-nova-border bg-nova-card';
  return (
    <div className={`rounded-lg shadow-sm border p-4 ${cls}`}>
      <p className='text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground'>
        {label}
      </p>
      <p className='mt-1 text-2xl font-semibold tabular-nums text-nova-foreground'>
        {value}
      </p>
      {hint ? <p className='mt-1 text-xs text-nova-muted-foreground'>{hint}</p> : null}
    </div>
  );
}

function parseError(err: unknown): string {
  if (err instanceof ApiError && err.bodyText) {
    try {
      const j = JSON.parse(err.bodyText) as { detail?: string };
      if (typeof j.detail === 'string') return j.detail;
    } catch {
      /* ignore */
    }
    return err.bodyText;
  }
  return err instanceof Error ? err.message : 'Request failed';
}

type GranularTab = 'terms' | 'actions' | 'snapshots';

export function AdsOpsPanel({ days }: { days: number }) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<GranularTab>('terms');
  const didAutoOpenActions = useRef(false);
  const [budgetUsd, setBudgetUsd] = useState('450');
  const [maxDailyUsd, setMaxDailyUsd] = useState('');
  const [maxCpcUsd, setMaxCpcUsd] = useState('2');
  const [competitorCpcUsd, setCompetitorCpcUsd] = useState('1');
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const settingsQuery = useQuery({
    queryKey: ['admin', 'ads-ops-settings'],
    queryFn: () => apiJson<AdsOpsSettings>(getToken, '/v1/admin/ads/ops/settings'),
  });

  const scorecardQuery = useQuery({
    queryKey: ['admin', 'ads-ops-scorecard', days],
    queryFn: () =>
      apiJson<AdsOpsScorecard>(
        getToken,
        `/v1/admin/ads/ops/scorecard?days=${days}`,
      ),
  });

  const termsQuery = useQuery({
    queryKey: ['admin', 'ads-ops-search-terms', days],
    queryFn: () =>
      apiJson<AdsOpsSearchTermsResponse>(
        getToken,
        `/v1/admin/ads/ops/search-terms?days=${days}`,
      ),
    enabled: tab === 'terms',
  });

  const actionsQuery = useQuery({
    queryKey: ['admin', 'ads-ops-actions'],
    queryFn: () =>
      apiJson<AdsOpsActionsResponse>(getToken, '/v1/admin/ads/ops/actions?limit=40'),
  });

  const snapshotsQuery = useQuery({
    queryKey: ['admin', 'ads-ops-snapshots'],
    queryFn: () =>
      apiJson<AdsOpsSnapshotsResponse>(
        getToken,
        '/v1/admin/ads/ops/snapshots?limit=14',
      ),
    enabled: tab === 'snapshots',
  });

  const standingOrdersQuery = useQuery({
    queryKey: ['admin', 'ads-ops-standing-orders'],
    queryFn: () =>
      apiJson<AdsOpsStandingOrdersResponse>(
        getToken,
        '/v1/admin/ads/ops/standing-orders',
      ),
  });

  useEffect(() => {
    const s = settingsQuery.data;
    if (!s) return;
    setBudgetUsd(String(s.monthly_budget_usd));
    setMaxDailyUsd(s.max_daily_cents != null ? String(s.max_daily_cents / 100) : '');
    setMaxCpcUsd(String(s.max_cpc_usd));
    setCompetitorCpcUsd(String(s.competitor_cpc_usd));
    setNotes(s.notes ?? '');
  }, [settingsQuery.data]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'ads-ops-settings'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'ads-ops-scorecard'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'ads-ops-actions'] });
  };

  const patchMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiJson<AdsOpsSettings>(getToken, '/v1/admin/ads/ops/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      setFormError(null);
      invalidate();
    },
    onError: (err) => setFormError(parseError(err)),
  });

  const applyMutation = useMutation({
    mutationFn: (id: number) =>
      apiJson<AdsOpsAction>(getToken, `/v1/admin/ads/ops/actions/${id}/apply`, {
        method: 'POST',
      }),
    onSuccess: () => invalidate(),
    onError: (err) => setFormError(parseError(err)),
  });

  const rejectMutation = useMutation({
    mutationFn: (id: number) =>
      apiJson<AdsOpsAction>(getToken, `/v1/admin/ads/ops/actions/${id}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      }),
    onSuccess: () => invalidate(),
    onError: (err) => setFormError(parseError(err)),
  });

  const saveKnobs = () => {
    const monthly = Math.round(Number(budgetUsd) * 100);
    if (!Number.isFinite(monthly) || monthly < 10000 || monthly > 200000) {
      setFormError('Monthly budget must be between $100 and $2,000.');
      return;
    }
    const maxCpc = Math.round(Number(maxCpcUsd) * 100);
    const competitor = Math.round(Number(competitorCpcUsd) * 100);
    const body: Record<string, unknown> = {
      monthly_budget_cents: monthly,
      max_cpc_cents: maxCpc,
      competitor_cpc_cents: competitor,
      notes,
    };
    if (maxDailyUsd.trim() === '') {
      body.clear_max_daily = true;
    } else {
      body.max_daily_cents = Math.round(Number(maxDailyUsd) * 100);
    }
    track('Ads Admin Knobs Saved');
    patchMutation.mutate(body);
  };

  const settings = settingsQuery.data;
  const card = scorecardQuery.data;
  const harbor = card?.harbor;
  const harborReddit = card?.harbor_reddit;
  const harborFacebook = card?.harbor_facebook;
  const cac = card?.harbor_cac_usd ?? null;
  const pending = useMemo(
    () =>
      (actionsQuery.data?.actions ?? []).filter((a) => a.status === 'proposed'),
    [actionsQuery.data?.actions],
  );
  const pendingCount = pending.length;

  useEffect(() => {
    if (didAutoOpenActions.current || pendingCount === 0) return;
    didAutoOpenActions.current = true;
    setTab('actions');
  }, [pendingCount]);

  const applyAction = (id: number) => {
    const action = (actionsQuery.data?.actions ?? []).find((row) => row.id === id);
    track('Ads Admin Action Applied', { kind: action?.kind });
    applyMutation.mutate(id);
  };

  const rejectAction = (id: number) => {
    const action = (actionsQuery.data?.actions ?? []).find((row) => row.id === id);
    track('Ads Admin Action Rejected', { kind: action?.kind });
    rejectMutation.mutate(id);
  };

  const busyId =
    applyMutation.isPending
      ? applyMutation.variables
      : rejectMutation.isPending
        ? rejectMutation.variables
        : undefined;

  return (
    <div className='space-y-4'>
      <StandingOrdersCard
        policy={standingOrdersQuery.data?.policy}
        playbook={standingOrdersQuery.data?.playbook}
        loading={standingOrdersQuery.isLoading}
        error={standingOrdersQuery.isError}
      />

      <div className='rounded-lg border border-nova-border bg-nova-card p-4 shadow-sm'>
        <div className='flex flex-wrap items-start justify-between gap-3'>
          <div>
            <h2 className='text-sm font-semibold text-nova-foreground'>Your job</h2>
            <p className='mt-1 text-sm text-nova-muted-foreground max-w-3xl'>
              This page plus talking to an agent is ads HQ. Set the monthly
              budget and caps. Pause the weekly agent if something looks wrong.
              Approve or reject proposed actions in the queue below while
              “Require approval” is on. Daily Celery only writes a scorecard
              snapshot — it does not change campaigns. Landing pages, RSA copy,
              and the monthly $30–$40 CAC go/no-go stay with you. The agent
              does not create or delete campaigns, and cannot invent keywords.
            </p>
          </div>
          {settings ? (
            <span
              className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold ${
                settings.agent_paused
                  ? 'bg-amber-100 text-amber-900'
                  : 'bg-emerald-100 text-emerald-800'
              }`}
            >
              {settings.agent_paused ? (
                <FiPause className='h-3.5 w-3.5' />
              ) : (
                <FiPlay className='h-3.5 w-3.5' />
              )}
              Agent {settings.agent_paused ? 'paused' : 'armed'}
            </span>
          ) : null}
        </div>

        <div className='mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4'>
          <label className='block text-xs font-semibold text-nova-muted-foreground'>
            Monthly budget (USD)
            <input
              type='number'
              min={100}
              max={2000}
              step={10}
              value={budgetUsd}
              onChange={(e) => setBudgetUsd(e.target.value)}
              className='mt-1 w-full rounded-md border border-nova-input px-3 py-2 text-sm font-normal text-nova-foreground'
            />
          </label>
          <label className='block text-xs font-semibold text-nova-muted-foreground'>
            Daily cap (USD, optional)
            <input
              type='number'
              min={1}
              step={1}
              placeholder='auto = monthly / days'
              value={maxDailyUsd}
              onChange={(e) => setMaxDailyUsd(e.target.value)}
              className='mt-1 w-full rounded-md border border-nova-input px-3 py-2 text-sm font-normal text-nova-foreground'
            />
          </label>
          <label className='block text-xs font-semibold text-nova-muted-foreground'>
            Max CPC (USD)
            <input
              type='number'
              min={0.1}
              step={0.1}
              value={maxCpcUsd}
              onChange={(e) => setMaxCpcUsd(e.target.value)}
              className='mt-1 w-full rounded-md border border-nova-input px-3 py-2 text-sm font-normal text-nova-foreground'
            />
          </label>
          <label className='block text-xs font-semibold text-nova-muted-foreground'>
            Competitor CPC cap (USD)
            <input
              type='number'
              min={0.1}
              step={0.1}
              value={competitorCpcUsd}
              onChange={(e) => setCompetitorCpcUsd(e.target.value)}
              className='mt-1 w-full rounded-md border border-nova-input px-3 py-2 text-sm font-normal text-nova-foreground'
            />
          </label>
        </div>

        <label className='mt-3 block text-xs font-semibold text-nova-muted-foreground'>
          Notes for the weekly agent
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className='mt-1 w-full rounded-md border border-nova-input px-3 py-2 text-sm font-normal text-nova-foreground'
            placeholder='e.g. Pause competitor snipe this week. Prefer weekonweekoff.com landings.'
          />
        </label>

        <div className='mt-3 flex flex-wrap items-center gap-3'>
          <button
            type='button'
            onClick={saveKnobs}
            disabled={patchMutation.isPending}
            className='rounded-md bg-nova-primary px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60'
          >
            {patchMutation.isPending ? 'Saving…' : 'Save knobs'}
          </button>
          {settings ? (
            <>
              <button
                type='button'
                onClick={() => {
                  const nextPaused = !settings.agent_paused;
                  track(
                    nextPaused
                      ? 'Ads Admin Agent Paused'
                      : 'Ads Admin Agent Unpaused',
                  );
                  patchMutation.mutate({ agent_paused: nextPaused });
                }}
                className='rounded-md border border-nova-input bg-nova-card px-3 py-1.5 text-xs font-semibold text-nova-foreground'
              >
                {settings.agent_paused ? 'Unpause agent' : 'Pause agent'}
              </button>
              <label className='inline-flex items-center gap-2 text-xs text-nova-foreground'>
                <input
                  type='checkbox'
                  checked={settings.require_human_approval}
                  onChange={(e) => {
                    track('Ads Admin Approval Toggled', {
                      required: e.target.checked,
                    });
                    patchMutation.mutate({
                      require_human_approval: e.target.checked,
                    });
                  }}
                />
                Require approval
              </label>
              <label className='inline-flex items-center gap-2 text-xs text-nova-foreground'>
                <input
                  type='checkbox'
                  checked={settings.upload_offline_conversions}
                  onChange={(e) => {
                    track('Ads Admin iOS Upload Toggled', {
                      enabled: e.target.checked,
                    });
                    patchMutation.mutate({
                      upload_offline_conversions: e.target.checked,
                    });
                  }}
                />
                Upload iOS gclid conversions
              </label>
            </>
          ) : null}
          {formError ? (
            <span className='text-xs text-rose-700'>{formError}</span>
          ) : null}
        </div>
        {settings?.updated_at ? (
          <p className='mt-2 text-xs text-nova-muted-foreground'>
            Last change {new Date(settings.updated_at).toLocaleString()}
            {settings.updated_by ? ` · ${settings.updated_by}` : ''}
            {settings ? ` · derived daily ${money(settings.derived_daily_budget_usd)}` : ''}
          </p>
        ) : null}

        {actionsQuery.isLoading ? (
          <p className='mt-4 text-sm text-nova-muted-foreground'>Loading proposed actions…</p>
        ) : pendingCount > 0 ? (
          <div className='mt-4 rounded-md border border-amber-200 bg-amber-50/70 p-3'>
            <p className='text-xs font-semibold uppercase tracking-wide text-amber-900'>
              Needs your approval ({pendingCount})
            </p>
            <ul className='mt-2 divide-y divide-amber-100'>
              {pending.map((a) => (
                <li
                  key={a.id}
                  className='flex flex-wrap items-start justify-between gap-3 py-2.5 first:pt-1 last:pb-0'
                >
                  <div className='min-w-0 max-w-2xl'>
                    <p className='text-sm text-nova-foreground'>{a.summary}</p>
                    {a.hypothesized_outcome ? (
                      <p className='mt-0.5 text-xs text-nova-muted-foreground'>
                        {a.hypothesized_outcome}
                      </p>
                    ) : null}
                  </div>
                  <div className='flex shrink-0 gap-2'>
                    <button
                      type='button'
                      disabled={busyId === a.id}
                      onClick={() => applyAction(a.id)}
                      className='rounded-md bg-nova-primary px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50'
                    >
                      Apply
                    </button>
                    <button
                      type='button'
                      disabled={busyId === a.id}
                      onClick={() => rejectAction(a.id)}
                      className='rounded-md border border-nova-input bg-nova-card px-3 py-1.5 text-xs font-semibold text-nova-foreground disabled:opacity-50'
                    >
                      Reject
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : settings?.require_human_approval ? (
          <p className='mt-4 text-xs text-nova-muted-foreground'>
            No proposed actions waiting.
          </p>
        ) : null}
      </div>

      <div>
        <h2 className='text-sm font-semibold text-nova-foreground'>Harbor-truth</h2>
        <p className='mt-1 text-xs text-nova-muted-foreground'>
          North star is RevenueCat paid (gross &gt; 0) on Google-attributed
          users. The paid tile below is still the scorecard proxy
          (subscribed / product id / Stripe). Ads-UI conversions are a
          footnote — do not optimize them.
        </p>
        {scorecardQuery.isLoading ? (
          <p className='mt-3 text-sm text-nova-muted-foreground'>Loading scorecard…</p>
        ) : scorecardQuery.isError || !card ? (
          <p className='mt-3 text-sm text-rose-700'>Could not load scorecard.</p>
        ) : (
          <div className='mt-3 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-7 gap-3'>
            <Tile
              label='Harbor CAC'
              value={money(cac)}
              hint={`Target ${money(card.target_cac_usd.low, 0)}–${money(card.target_cac_usd.high, 0)}`}
              tone={cacTone(cac, card.target_cac_usd.high)}
            />
            <Tile
              label='Google spend'
              value={money(card.google_ads.spend_usd)}
              hint={card.google_ads.error ? 'Ads API issue — Harbor counts still live' : `${days}d`}
              tone='highlight'
            />
            <Tile
              label='Google payers'
              value={String(harbor?.paid ?? 0)}
              hint={`${harbor?.attributed ?? 0} attributed · ${harbor?.landed ?? 0} landed`}
            />
            <Tile
              label='Attr → paid'
              value={
                harbor?.attr_to_paid_rate != null
                  ? `${(harbor.attr_to_paid_rate * 100).toFixed(0)}%`
                  : '—'
              }
            />
            <Tile
              label='Expected MTD'
              value={money(card.pacing.expected_mtd_usd)}
              hint={`Day ${card.pacing.day_of_month}/${card.pacing.days_in_month} of ${money(card.pacing.month_budget_usd, 0)}`}
            />
            <Tile
              label='Ads-UI conv.'
              value={
                card.google_ads.ads_ui_conversions != null
                  ? card.google_ads.ads_ui_conversions.toFixed(1)
                  : '—'
              }
              hint='Do not optimize this'
            />
            <Tile
              label='Reddit payers'
              value={String(harborReddit?.paid ?? 0)}
              hint={`${harborReddit?.attributed ?? 0} attributed · ${harborReddit?.landed ?? 0} landed · ${harborReddit?.landed_rdt_cid ?? 0} w/ rdt_cid`}
            />
            <Tile
              label='Meta / IG payers'
              value={String(harborFacebook?.paid ?? 0)}
              hint={`${harborFacebook?.attributed ?? 0} attributed · ${harborFacebook?.landed ?? 0} landed · ${harborFacebook?.landed_fbclid ?? 0} w/ fbclid`}
            />
            <Tile
              label='Meta spend'
              value={money(card.meta_ads?.spend_usd)}
              hint={
                card.meta_ads?.error
                  ? card.meta_ads.error
                  : `${card.meta_ads?.ads_ui_leads ?? 0} Ads-UI Leads`
              }
            />
          </div>
        )}
        {card?.google_ads.error ? (
          <p className='mt-2 flex items-start gap-1.5 text-xs text-amber-800'>
            <FiAlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
            {card.google_ads.error}
          </p>
        ) : null}
      </div>

      <div className='rounded-lg border border-nova-border bg-nova-card shadow-sm overflow-hidden'>
        <div className='flex flex-wrap items-center justify-between gap-2 border-b border-nova-border px-4 py-3'>
          <h2 className='text-sm font-semibold text-nova-foreground'>Granular</h2>
          <div className='inline-flex rounded-md border border-nova-border bg-nova-card p-0.5'>
            {(
              [
                ['terms', 'Search terms'],
                ['actions', pendingCount ? `Actions (${pendingCount})` : 'Actions'],
                ['snapshots', 'Snapshots'],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type='button'
                onClick={() => setTab(id)}
                className={`px-3 py-1.5 text-xs font-semibold rounded ${
                  tab === id ? 'bg-nova-accent text-nova-primary' : 'text-nova-muted-foreground'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {tab === 'terms' ? (
          <TermsTable query={termsQuery} />
        ) : tab === 'actions' ? (
          <ActionsTable
            actions={actionsQuery.data?.actions ?? []}
            loading={actionsQuery.isLoading}
            onApply={applyAction}
            onReject={rejectAction}
            busyId={busyId}
          />
        ) : (
          <SnapshotsTable
            snapshots={snapshotsQuery.data?.snapshots ?? []}
            loading={snapshotsQuery.isLoading}
          />
        )}
      </div>
    </div>
  );
}

function StandingOrdersCard({
  policy,
  playbook,
  loading,
  error,
}: {
  policy?: AdsOpsPolicyDigest;
  playbook?: string;
  loading: boolean;
  error: boolean;
}) {
  const grouped = useMemo(
    () => groupAllowedKeywords(policy?.allowed_keywords ?? []),
    [policy?.allowed_keywords],
  );

  return (
    <div className='rounded-lg border border-nova-primary/20 bg-nova-accent/60 p-4 shadow-sm'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div className='min-w-0 max-w-3xl'>
          <h2 className='text-sm font-semibold text-nova-foreground'>Standing orders</h2>
          <p className='mt-1 text-sm text-nova-foreground'>
            {policy?.north_star ??
              'RevenueCat paid on Google-attributed users. Ads-UI conversions are a footnote.'}
          </p>
        </div>
        {policy ? (
          <p className='text-xs text-nova-muted-foreground'>
            {policy.learnings}
            {policy.campaign_id ? ` · campaign ${policy.campaign_id}` : ''}
            {policy.shared_negative_set_id
              ? ` · no free ${policy.shared_negative_set_id}`
              : ''}
            {policy.bidding ? ` · ${policy.bidding}` : ''}
          </p>
        ) : null}
      </div>

      {loading ? (
        <p className='mt-3 text-sm text-nova-muted-foreground'>Loading standing orders…</p>
      ) : error || !policy ? (
        <p className='mt-3 text-sm text-rose-700'>
          Could not load standing orders.
        </p>
      ) : (
        <div className='mt-4 grid gap-4 xl:grid-cols-2'>
          <div>
            <p className='text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground'>
              Do this
            </p>
            <ul className='mt-2 list-disc space-y-1.5 pl-5 text-sm text-nova-foreground'>
              {(policy.hq_orders.length > 0
                ? policy.hq_orders
                : [playbook ?? 'Follow ads/ADS_LEARNINGS.md.']
              ).map((order) => (
                <li key={order}>{order}</li>
              ))}
            </ul>
            <p className='mt-3 text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground'>
              Never add
            </p>
            <div className='mt-2 flex flex-wrap gap-1.5'>
              {policy.never_add.map((stem) => (
                <span
                  key={stem}
                  className='rounded-md bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-900'
                >
                  {stem}
                </span>
              ))}
            </div>
            <p className='mt-3 text-xs text-nova-muted-foreground'>
              Agent may {policy.allowed_mutate_kinds.join(', ')}.
              {policy.allow_campaign_create || policy.allow_campaign_delete
                ? ''
                : ' Cannot create or delete campaigns.'}{' '}
              New stems need a PR to keywords.yaml — talk to an agent.
            </p>
          </div>
          <div>
            <p className='text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground'>
              Allowed keywords
            </p>
            <p className='mt-1 text-xs text-nova-muted-foreground'>
              Expense and Calendar are the golden-age groups. Intent stays live;
              stop leaks with negatives.
            </p>
            <div className='mt-2 space-y-2'>
              {grouped.map((group) => {
                const openByDefault =
                  group.name === 'expense' || group.name === 'calendar';
                return (
                  <details
                    key={group.name}
                    open={openByDefault}
                    className='rounded-md border border-nova-border bg-nova-card px-3 py-2'
                  >
                    <summary className='cursor-pointer text-xs font-semibold text-nova-foreground'>
                      {titleCaseGroup(group.name)}
                      <span className='ml-1.5 font-normal text-nova-muted-foreground'>
                        ({group.keywords.length})
                      </span>
                    </summary>
                    <ul className='mt-2 space-y-1'>
                      {group.keywords.map((kw) => (
                        <li
                          key={kw.text}
                          className='flex flex-wrap items-baseline justify-between gap-2 text-sm'
                        >
                          <span className='font-mono text-[13px] text-nova-foreground'>
                            {kw.text}
                          </span>
                          <span className='text-[11px] font-semibold uppercase tracking-wide text-nova-muted-foreground'>
                            {kw.matches.join(' + ')}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </details>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TermsTable({
  query,
}: {
  query: { data?: AdsOpsSearchTermsResponse; isLoading: boolean };
}) {
  if (query.isLoading) {
    return <p className='px-4 py-4 text-sm text-nova-muted-foreground'>Loading search terms…</p>;
  }
  const data = query.data;
  if (!data?.ok) {
    return (
      <p className='px-4 py-4 text-sm text-amber-800'>
        {data?.error || 'Search terms unavailable.'}
      </p>
    );
  }
  if (data.terms.length === 0) {
    return <p className='px-4 py-4 text-sm text-nova-muted-foreground'>No clicked search terms in range.</p>;
  }
  return (
    <div className='overflow-x-auto'>
      <table className='w-full text-sm'>
        <thead className='bg-nova-muted text-xs uppercase text-nova-muted-foreground'>
          <tr>
            <th className='px-4 py-2 text-left'>Query</th>
            <th className='px-4 py-2 text-right'>Clicks</th>
            <th className='px-4 py-2 text-right'>Spend</th>
            <th className='px-4 py-2 text-right'>Ads-UI conv.</th>
          </tr>
        </thead>
        <tbody>
          {data.terms.map((t) => (
            <tr key={t.search_term} className='border-t border-nova-border'>
              <td className='px-4 py-2 text-nova-foreground'>{t.search_term}</td>
              <td className='px-4 py-2 text-right tabular-nums'>{t.clicks}</td>
              <td className='px-4 py-2 text-right tabular-nums'>{microsUsd(t.cost_micros)}</td>
              <td className='px-4 py-2 text-right tabular-nums text-nova-muted-foreground'>
                {t.conversions.toFixed(1)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ActionsTable({
  actions,
  loading,
  onApply,
  onReject,
  busyId,
}: {
  actions: AdsOpsAction[];
  loading: boolean;
  onApply: (id: number) => void;
  onReject: (id: number) => void;
  busyId?: number;
}) {
  if (loading) {
    return <p className='px-4 py-4 text-sm text-nova-muted-foreground'>Loading actions…</p>;
  }
  if (actions.length === 0) {
    return (
      <p className='px-4 py-4 text-sm text-nova-muted-foreground'>
        No actions yet. Saving knobs writes the first ledger row.
      </p>
    );
  }
  return (
    <div className='overflow-x-auto'>
      <table className='w-full text-sm'>
        <thead className='bg-nova-muted text-xs uppercase text-nova-muted-foreground'>
          <tr>
            <th className='px-4 py-2 text-left'>When</th>
            <th className='px-4 py-2 text-left'>Who</th>
            <th className='px-4 py-2 text-left'>Kind</th>
            <th className='px-4 py-2 text-left'>Status</th>
            <th className='px-4 py-2 text-left'>Summary / why</th>
            <th className='px-4 py-2 text-right'> </th>
          </tr>
        </thead>
        <tbody>
          {actions.map((a) => (
            <tr key={a.id} className='border-t border-nova-border'>
              <td className='px-4 py-2 text-nova-muted-foreground whitespace-nowrap'>
                {new Date(a.created_at).toLocaleString()}
              </td>
              <td className='px-4 py-2'>{a.actor}</td>
              <td className='px-4 py-2'>
                <span className='rounded bg-nova-muted px-1.5 py-0.5 text-[11px] font-semibold'>
                  {a.kind}
                </span>
              </td>
              <td className='px-4 py-2'>
                <span
                  className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${
                    a.status === 'proposed'
                      ? 'bg-amber-100 text-amber-900'
                      : a.status === 'applied'
                        ? 'bg-emerald-100 text-emerald-800'
                        : a.status === 'rejected'
                          ? 'bg-nova-muted text-nova-foreground'
                          : 'bg-rose-100 text-rose-800'
                  }`}
                >
                  {a.status}
                </span>
              </td>
              <td className='px-4 py-2 text-nova-foreground'>
                <div>{a.summary}</div>
                {a.hypothesized_outcome ? (
                  <p className='mt-0.5 text-xs text-nova-muted-foreground'>
                    {a.hypothesized_outcome}
                  </p>
                ) : null}
              </td>
              <td className='px-4 py-2 text-right whitespace-nowrap'>
                {a.status === 'proposed' ? (
                  <div className='inline-flex gap-2'>
                    <button
                      type='button'
                      disabled={busyId === a.id}
                      onClick={() => onApply(a.id)}
                      className='rounded-md bg-nova-primary px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50'
                    >
                      Apply
                    </button>
                    <button
                      type='button'
                      disabled={busyId === a.id}
                      onClick={() => onReject(a.id)}
                      className='rounded-md border border-nova-input bg-nova-card px-3 py-1.5 text-xs font-semibold text-nova-foreground disabled:opacity-50'
                    >
                      Reject
                    </button>
                  </div>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SnapshotsTable({
  snapshots,
  loading,
}: {
  snapshots: AdsOpsSnapshotsResponse['snapshots'];
  loading: boolean;
}) {
  if (loading) {
    return <p className='px-4 py-4 text-sm text-nova-muted-foreground'>Loading snapshots…</p>;
  }
  if (snapshots.length === 0) {
    return (
      <p className='px-4 py-4 text-sm text-nova-muted-foreground'>
        Daily snapshots appear after the 06:00 UTC Celery job runs
        (read-only scorecard — no campaign mutates).
      </p>
    );
  }
  return (
    <ul className='divide-y divide-nova-border'>
      {snapshots.map((s) => {
        const paid = (s.payload as { harbor?: { paid?: number } })?.harbor?.paid;
        const cac = (s.payload as { harbor_cac_usd?: number | null })?.harbor_cac_usd;
        return (
          <li key={s.id} className='px-4 py-3 text-sm flex flex-wrap justify-between gap-2'>
            <span className='text-nova-muted-foreground'>
              {new Date(s.created_at).toLocaleString()} · {s.range_days}d
            </span>
            <span className='text-nova-foreground'>
              {paid ?? '—'} payers · CAC {money(cac ?? null)}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

